import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BusinessCard from "../components/BusinessCard";
import { ConnectionBadge, HEALTH } from "../components/connectionStatus";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Building2, CheckCircle2, Wrench, Clock, Loader2, Users, UserPlus, ShoppingCart, DollarSign, Activity, AlertTriangle, PlugZap, ExternalLink, RefreshCw, Check } from "lucide-react";
import { motion } from "framer-motion";

function StatCard({ icon: Icon, label, value, tint, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="bg-white border border-gray-200 rounded-xl p-6"
      data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${tint}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="mt-4 font-heading text-3xl font-bold tracking-tight">{value}</p>
      <p className="text-sm text-gray-500 mt-1">{label}</p>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [eco, setEco] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const loadEco = () => api.get("/ecosystem/dashboard").then((r) => setEco(r.data)).catch(() => setEco(false));

  useEffect(() => {
    api.get("/businesses").then((r) => setBusinesses(r.data)).finally(() => setLoading(false));
    loadEco();
  }, []);

  const runHealthCheck = async () => {
    setChecking(true);
    try { await api.post("/ecosystem/health-check"); await loadEco(); toast.success("Operational status refreshed"); }
    catch (e) { toast.error("Status check failed"); }
    finally { setChecking(false); }
  };

  const ack = async (id) => {
    try { await api.post(`/ecosystem/events/${id}/ack`); await loadEco(); }
    catch (e) { toast.error("Could not mark as handled"); }
  };

  const connMap = eco ? Object.fromEntries(eco.entities.map((e) => [e.id, e.connection_status])) : {};
  const none = eco && eco.summary.entities_connected === 0;

  const active = businesses.filter((b) => b.status === "Active").length;
  const setup = businesses.filter((b) => b.status === "Setup Required").length;
  const coming = businesses.filter((b) => b.status === "Coming Soon").length;
  const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="max-w-7xl">
      <p className="text-xs uppercase tracking-widest text-[#581C87] font-semibold">
        Executive Command Center
      </p>
      <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-2">
        Welcome, {user?.name?.replace(/^(Dr\.?|Mr\.?|Mrs\.?|Ms\.?)\s+/i, "").split(" ")[0] || "Admin"}
      </h1>
      <p className="text-gray-500 mt-2">
        Your central control plane for every business operating system.
      </p>

      {/* ---- Ecosystem performance (CEO command center) ---- */}
      {eco === false && (
        <div className="mt-8 rounded-xl px-5 py-4 bg-gray-50 border border-gray-200 text-sm text-gray-600" data-testid="ecosystem-data-unavailable">
          Ecosystem data is unavailable right now. Figures are hidden rather than shown as zero.
        </div>
      )}
      {eco && (
        <div className="mt-8" data-testid="ecosystem-overview">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="font-heading text-xl font-bold tracking-tight">Ecosystem Performance</h2>
            <span className="text-xs text-gray-400">Last {eco.window_days} days · verified signed events only</span>
          </div>

          <div
            className={`rounded-xl px-5 py-4 mb-4 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm ${eco.summary.entities_need_attention > 0 ? "bg-amber-50 border border-amber-200" : none ? "bg-gray-50 border border-gray-200" : "bg-emerald-50 border border-emerald-200"}`}
            data-testid="ecosystem-summary-banner"
          >
            <span className="font-semibold text-gray-800">
              {none ? "No businesses are data-connected yet" : `${eco.summary.entities_normal} of ${eco.summary.entities_connected} connected businesses operating normally`}
            </span>
            <span className={eco.summary.attention_count > 0 ? "text-amber-700 font-semibold" : "text-emerald-700"}>
              {eco.summary.attention_count} item{eco.summary.attention_count === 1 ? "" : "s"} need your attention
            </span>
            <span className="text-gray-500">{eco.summary.entities_total - eco.summary.entities_connected} not connected (shown as —, not $0)</span>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            <StatCard index={0} icon={Users} label="Leads / Inquiries" value={none ? "—" : eco.totals.lead} tint="bg-blue-50 text-blue-600" />
            <StatCard index={1} icon={UserPlus} label="Registrations" value={none ? "—" : eco.totals.registration} tint="bg-[#F3E8FF] text-[#6B21A8]" />
            <StatCard index={2} icon={ShoppingCart} label="Sales" value={none ? "—" : eco.totals.sale} tint="bg-purple-50 text-purple-600" />
            <StatCard index={3} icon={DollarSign} label="Revenue" value={none ? "—" : money(eco.totals.revenue)} tint="bg-emerald-50 text-emerald-600" />
            <StatCard index={4} icon={Activity} label="Recent Activity" value={eco.recent_activity.length} tint="bg-slate-100 text-slate-600" />
            <StatCard index={5} icon={AlertTriangle} label="Needs Attention" value={eco.summary.attention_count} tint="bg-amber-50 text-amber-600" />
          </div>

          {eco.attention.length > 0 && (
            <div className="mt-4 bg-white border border-amber-200 rounded-xl p-5" data-testid="ecosystem-attention">
              <p className="text-xs uppercase tracking-widest text-amber-600 font-semibold flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Needs Your Attention
              </p>
              <div className="mt-3 space-y-1.5">
                {eco.attention.map((a) => (
                  <div key={a.id} className="text-sm text-gray-700 flex items-center justify-between gap-3 border-b border-gray-50 py-1.5" data-testid={`attention-item-${a.id}`}>
                    <span><span className="font-medium">{a.business_name}:</span> {a.message}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-gray-400">{a.timestamp ? new Date(a.timestamp).toLocaleDateString() : ""}</span>
                      {a.ackable && (
                        <Button variant="outline" size="sm" className="h-7 border-gray-200" onClick={() => ack(a.id)} data-testid={`ack-${a.id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Handled
                        </Button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="entity-signals">
            <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
              <PlugZap className="h-4 w-4 text-[#581C87]" />
              <span className="font-heading font-bold text-sm flex-1">Entity Signals</span>
              <Button variant="outline" size="sm" className="h-8 border-gray-200" onClick={runHealthCheck} disabled={checking} data-testid="run-health-check-button">
                {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Check operational status
              </Button>
            </div>
            <div className="divide-y divide-gray-50">
              {eco.entities.map((e) => {
                const connected = e.connection_status !== "not_connected";
                const h = e.health ? HEALTH[e.health.state] : null;
                return (
                  <div key={e.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`entity-signal-${e.id}`}>
                    <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#581C87] to-[#7C3AED] flex items-center justify-center text-white text-[11px] font-bold shrink-0">{e.logo}</div>
                    <span className="font-medium text-sm text-gray-800 flex-1 min-w-[160px]">
                      {e.name}
                      {e.os_url && (
                        <a href={e.os_url} target="_blank" rel="noopener noreferrer" className="ml-2 inline-flex text-[#581C87] align-middle" title="Open Operating System" data-testid={`entity-open-os-${e.id}`}>
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </span>
                    <span className={`text-[11px] ${h ? h.cls : "text-gray-400"}`} data-testid={`op-status-${e.id}`}>
                      {h ? h.label : e.os_url ? "Not checked" : "No OS configured"}
                    </span>
                    <ConnectionBadge status={e.connection_status} testId={`conn-status-${e.id}`} />
                    {connected ? (
                      <span className="text-xs text-gray-500 font-mono" data-testid={`entity-counts-${e.id}`}>
                        {e.counts.lead}L · {e.counts.registration}R · {e.counts.sale}S · {money(e.counts.revenue)}
                        {e.counts.payment_failed > 0 && <span className="text-red-600 font-semibold"> · {e.counts.payment_failed} failed</span>}
                        {e.open_alerts > 0 && <span className="text-amber-600 font-semibold"> · {e.open_alerts} open</span>}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-300 font-mono">—</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        <StatCard index={0} icon={Building2} label="Total Businesses" value={businesses.length} tint="bg-[#F3E8FF] text-[#6B21A8]" />
        <StatCard index={1} icon={CheckCircle2} label="Active" value={active} tint="bg-[#ECFDF5] text-[#047857]" />
        <StatCard index={2} icon={Wrench} label="Setup Required" value={setup} tint="bg-[#F3E8FF] text-[#6B21A8]" />
        <StatCard index={3} icon={Clock} label="Coming Soon" value={coming} tint="bg-[#F3F4F6] text-[#4B5563]" />
      </div>

      <div className="flex items-center justify-between mt-12 mb-6">
        <h2 className="font-heading text-xl font-bold tracking-tight">Operating Systems</h2>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[#581C87]" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {businesses.map((b, i) => (
            <BusinessCard key={b.id} business={b} index={i} connection={eco === false ? "unavailable" : connMap[b.id]} />
          ))}
        </div>
      )}
    </div>
  );
}
